/**
 * commitmentViewService — the fetch half of the canonical commitment view (P3).
 *
 * PRD: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md §5.
 * Assembly + judgment: src/lib/commitmentViewCore.ts.
 *
 * READ-ONLY BY CONSTRUCTION. There is no write path in this module and there
 * must never be one: P3 is assembly over append-only records, and a write here
 * would make the canonical view a second place commitments can be edited —
 * exactly the drift it exists to expose.
 *
 * Every section is fetched independently and degrades on its own. A workspace
 * with no resource claims (prod holds zero rows today) must still render six
 * working sections and one honest absence, so a failed or empty read is
 * reported as "not on the record" by the core rather than failing the page.
 */

import { getSupabaseClient } from './supabaseClient';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import { listAlternativesForDecision } from './rejectedAlternativesService';
import {
  assembleCommitmentView,
  type CommitmentDecisionFacts,
  type CommitmentIdentity,
  type CommitmentView,
  type CommitmentVersionState,
} from './commitmentViewCore';

export interface CommitmentViewResult {
  view: CommitmentView | null;
  error?: string;
}

/**
 * Assembles one commitment.
 *
 * The identity is (plan cycle, item) — the same durable pair migrations 680
 * and 681 key their commitment terms and resource claims to, chosen there
 * precisely because it survives plan-version supersession.
 */
export async function fetchCommitmentView(
  identity: CommitmentIdentity,
): Promise<CommitmentViewResult> {
  const db = getTypedSupabaseClient();

  const { data: cycle, error: cycleError } = await db
    .from('plan_cycles')
    .select('id, name')
    .eq('id', identity.planCycleId)
    .single();
  if (cycleError || !cycle) {
    return { view: null, error: cycleError?.message ?? 'Plan cycle not found' };
  }

  const [item, versions, authority] = await Promise.all([
    fetchItem(identity.itemId),
    fetchVersionStates(identity),
    fetchAuthority(identity.planCycleId),
  ]);

  const decisions = await fetchDecisions(identity, versions);
  const [alternatives, terms, claims] = await Promise.all([
    fetchAlternatives(decisions),
    fetchTerms(identity),
    fetchClaims(identity),
  ]);

  return {
    view: assembleCommitmentView({
      identity,
      cycleName: cycle.name,
      item,
      versions,
      decisions,
      alternatives,
      terms,
      claims,
      authority,
    }),
  };
}

async function fetchItem(itemId: string) {
  const db = getTypedSupabaseClient();
  const { data } = await db
    .from('items')
    .select('id, title, description')
    .eq('id', itemId)
    .maybeSingle();
  if (!data) return null;
  return { itemId: data.id, title: data.title, description: data.description };
}

/**
 * The commitment's state in every plan version of the cycle that carries it.
 *
 * `initiatives` is the per-version row for an item; `plan_versions` supplies
 * the version number and the change provenance (mig 677's `change_reason` and
 * `source_decision_id`). Versions that DROPPED the item simply have no
 * initiative row, which is why `changeHistoryFor` treats a first appearance as
 * a change rather than inferring absence.
 */
async function fetchVersionStates(
  identity: CommitmentIdentity,
): Promise<CommitmentVersionState[]> {
  const db = getTypedSupabaseClient();

  const { data: planVersions } = await db
    .from('plan_versions')
    .select('id, version_number, created_at, change_reason, source_decision_id')
    .eq('plan_cycle_id', identity.planCycleId)
    .order('version_number', { ascending: true });
  if (!planVersions || planVersions.length === 0) return [];

  const { data: initiatives } = await db
    .from('initiatives')
    .select('plan_version_id, status, priority_position, certainty_level, drop_reason')
    .eq('item_id', identity.itemId)
    .in(
      'plan_version_id',
      planVersions.map((v) => v.id),
    );
  if (!initiatives || initiatives.length === 0) return [];

  const byVersion = new Map(planVersions.map((v) => [v.id, v]));

  const states: CommitmentVersionState[] = [];
  for (const row of initiatives) {
    const pv = byVersion.get(row.plan_version_id);
    if (!pv) continue;
    states.push({
      planVersionId: pv.id,
      versionNumber: pv.version_number,
      createdAt: pv.created_at,
      status: row.status,
      priorityPosition: row.priority_position,
      certaintyLevel: row.certainty_level,
      dropReason: row.drop_reason,
      changeReason: pv.change_reason,
      sourceDecisionId: pv.source_decision_id,
    });
  }
  return states;
}

/**
 * The decisions behind this commitment, from the two links that are real.
 *
 * 1. `plan_versions.source_decision_id` (mig 677) — the decision that caused a
 *    version carrying this commitment. This is the authoritative link.
 * 2. `decisions.item_id` — decisions memorialized against the item itself.
 *
 * Deliberately NOT `decisions.cycle_id = planCycleId`: that column carries a
 * prioritization cycle id, not a plan cycle id (it has no foreign key, and the
 * two id spaces are unrelated), so matching on it would attach another cycle's
 * decisions to this commitment whenever the ids happened to collide.
 */
async function fetchDecisions(
  identity: CommitmentIdentity,
  versions: CommitmentVersionState[],
): Promise<CommitmentDecisionFacts[]> {
  const db = getTypedSupabaseClient();
  const sourceIds = Array.from(
    new Set(versions.map((v) => v.sourceDecisionId).filter((id): id is string => Boolean(id))),
  );

  const columns =
    'id, title, decided_at, decided_by_name, rationale_context, rationale_reasoning, rationale_alternatives, reconsideration_conditions, session_id';

  const [byItem, bySource] = await Promise.all([
    db.from('decisions').select(columns).eq('item_id', identity.itemId),
    sourceIds.length > 0
      ? db.from('decisions').select(columns).in('id', sourceIds)
      : Promise.resolve({ data: [] as never[] }),
  ]);

  const merged = new Map<string, CommitmentDecisionFacts>();
  for (const row of [...(byItem.data ?? []), ...(bySource.data ?? [])]) {
    merged.set(row.id, {
      decisionId: row.id,
      title: row.title,
      decidedAt: row.decided_at,
      decidedByName: row.decided_by_name,
      rationaleContext: row.rationale_context,
      rationaleReasoning: row.rationale_reasoning,
      rationaleAlternatives: row.rationale_alternatives,
      reconsiderationConditions: row.reconsideration_conditions,
      sessionId: row.session_id,
    });
  }

  return Array.from(merged.values()).sort((a, b) =>
    (b.decidedAt ?? '').localeCompare(a.decidedAt ?? ''),
  );
}

async function fetchAlternatives(decisions: CommitmentDecisionFacts[]) {
  const results = await Promise.all(
    decisions.map((d) => listAlternativesForDecision(d.decisionId)),
  );
  // One item can only be beaten once per decision, but two decisions on the
  // same commitment can each have beaten it — dedupe on the item so the
  // narration does not name the same alternative twice.
  const byItem = new Map<string, (typeof results)[number]['rows'][number]>();
  for (const result of results) {
    for (const row of result.rows) {
      if (!byItem.has(row.itemId)) byItem.set(row.itemId, row);
    }
  }
  return Array.from(byItem.values());
}

async function fetchTerms(identity: CommitmentIdentity) {
  const db = getTypedSupabaseClient();
  const { data } = await db
    .from('plan_commitment_terms')
    .select('id, kind, condition_text, status, review_by')
    .eq('plan_cycle_id', identity.planCycleId)
    .eq('item_id', identity.itemId)
    .order('created_at', { ascending: true });

  return (data ?? []).map((row) => ({
    id: row.id,
    kind: row.kind,
    conditionText: row.condition_text,
    status: row.status,
    reviewBy: row.review_by,
  }));
}

/**
 * Declared resource claims for this commitment.
 *
 * Prod holds zero `plan_resource_claims` rows today, so the empty result is the
 * expected case rather than an edge one — the core renders it as an honest
 * absence. Superseded claims are excluded: the section answers "what does this
 * cost", not "what has it ever been said to cost".
 */
async function fetchClaims(identity: CommitmentIdentity) {
  const db = getTypedSupabaseClient();
  const { data } = await db
    .from('plan_resource_claims')
    .select('id, claim_kind, resource_type, declared_hours, declared_amount, currency, status')
    .eq('plan_cycle_id', identity.planCycleId)
    .eq('item_id', identity.itemId)
    .is('superseded_by_claim_id', null)
    .is('withdrawn_at', null)
    .order('created_at', { ascending: true });

  return (data ?? []).map((row) => ({
    id: row.id,
    claimKind: row.claim_kind,
    resourceType: row.resource_type,
    declaredHours: row.declared_hours,
    declaredAmount: row.declared_amount,
    currency: row.currency,
    status: row.status,
  }));
}

/**
 * The cycle's active authorization, plus P1's provenance readout.
 *
 * The provenance columns come from migration 698, which is written but applied
 * to no environment — so `sourceCycleReportId` and `sourceCycleReportBasis`
 * are both expected to be null everywhere today. That is the point: the view
 * renders the basis so a NULL reads as "unmeasured, and here is why" rather
 * than as a broken join (see porProvenanceBasis.ts).
 */
async function fetchAuthority(planCycleId: string) {
  const db = getTypedSupabaseClient();

  const { data: snapshot } = await db
    .from('plan_of_record_snapshots')
    .select('plan_version_id, authorized_at, authorized_by')
    .eq('plan_cycle_id', planCycleId)
    .is('superseded_by_snapshot_id', null)
    .maybeSingle();

  let authorizedVersionNumber: number | null = null;
  if (snapshot?.plan_version_id) {
    const { data: pv } = await db
      .from('plan_versions')
      .select('version_number')
      .eq('id', snapshot.plan_version_id)
      .maybeSingle();
    authorizedVersionNumber = pv?.version_number ?? null;
  }

  const [cycleReportId, basis] = await Promise.all([
    fetchNarrativeCycleReportId(planCycleId),
    fetchProvenanceBasis(planCycleId),
  ]);

  return {
    authorizedVersionNumber,
    authorizedAt: snapshot?.authorized_at ?? null,
    authorizedBy: snapshot?.authorized_by ?? null,
    sourceCycleReportId: cycleReportId,
    sourceCycleReportBasis: basis,
  };
}

async function fetchNarrativeCycleReportId(planCycleId: string): Promise<string | null> {
  const db = getTypedSupabaseClient();
  const { data: record } = await db
    .from('por_records')
    .select('id')
    .eq('plan_cycle_id', planCycleId)
    .order('is_primary', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!record) return null;

  const { data: snapshot } = await db
    .from('por_record_snapshots')
    .select('source_cycle_report_id')
    .eq('por_id', record.id)
    .is('superseded_by_snapshot_id', null)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();

  return snapshot?.source_cycle_report_id ?? null;
}

/**
 * Migration 698 records WHY a cycle-report provenance is or is not available,
 * in the authorization's audit payload. Read untyped: `payload` is Json, so the
 * basis key is not in the generated types either way.
 */
async function fetchProvenanceBasis(planCycleId: string): Promise<string | null> {
  const db = getSupabaseClient();
  const { data } = await db
    .from('planning_audit_events')
    .select('payload')
    .eq('plan_cycle_id', planCycleId)
    .eq('event_type', 'por_authorized')
    .order('occurred_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  const payload = (data as { payload?: Record<string, unknown> } | null)?.payload;
  const basis = payload?.source_cycle_report_basis;
  return typeof basis === 'string' ? basis : null;
}
